import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, GraduationCap, ArrowRight } from "lucide-react";

function buildSteps(data) {
  const { client, documents, items, disputes } = data;
  const hasId = documents.some((d) => d.type === "id_proof");
  const hasReport = documents.some((d) => d.type === "credit_report");
  const parsed = documents.some((d) => d.type === "credit_report" && d.parsed);
  const confirmed = items.some((i) => ["confirmed", "disputed", "resolved"].includes(i.status));
  const mailed = disputes.some((d) => ["mailed", "in_review", "responded", "resolved"].includes(d.status));
  const resolved = disputes.some((d) => d.status === "resolved");

  return [
    {
      key: "intake", title: "Get the intake form completed", done: !!client.intake_completed, tab: "overview", action: "View client info",
      what: "Every dispute letter is verified against the client's identity. The intake form collects their legal name, DOB, SSN, and current address.",
      how: "Your client fills this out in their own portal (Intake Form page). If you're on the phone with them, stay on the line until they submit it. You can check their answers on the Overview tab.",
      tip: "Letters with mismatched addresses get rejected by bureaus — double-check spelling.",
    },
    {
      key: "documents", title: "Collect their documents", done: hasId && hasReport, tab: "items", action: "Go to documents",
      what: "You need two things before any dispute: a government-issued photo ID and at least one credit report PDF (ideally all three bureaus).",
      how: "The client uploads these in their portal under My Documents, or you can upload for them here in the Negative Items tab upload box. A proof of address (utility bill or lease) makes letters stronger.",
      tip: "Clients can download free reports at annualcreditreport.com.",
    },
    {
      key: "agreement", title: "Get the service agreement signed", done: !!client.agreement_signed, tab: "overview", action: "Check status",
      what: "CROA law requires a signed agreement before you perform any credit repair work. No signature = no disputes.",
      how: "The client signs electronically in their portal under Agreement — they read the terms, type their name, and tick the consent box. This step checks itself off the moment they sign.",
      tip: "Remind them they have a 3-business-day cancellation right — saying it out loud builds trust.",
    },
    {
      key: "payment", title: "Collect the setup fee", done: !!client.setup_fee_paid, tab: "billing", action: "Open billing",
      what: "The setup fee invoice is created automatically when the client is added. Work shouldn't start until it's settled.",
      how: "Clients pay online with PayPal from their portal's Billing page. If they pay with CashApp, open the Billing tab here, find the pending invoice, click Record CashApp, and enter their $Cashtag as the reference.",
      tip: "Every payment lands in the audit log — you can always prove who paid what and when.",
    },
    {
      key: "analyze", title: "Run the AI report analysis", done: parsed || items.length > 0, tab: "items", action: "Go to items",
      what: "The AI reads the uploaded credit report and flags every negative item: collections, charge-offs, late payments, and unauthorized inquiries.",
      how: "In the Negative Items tab, find the uploaded report in the documents list and click 'Analyze with AI'. It takes about 30 seconds. Repeat for each bureau's report.",
      tip: "If the AI misses something, use 'Add manually' — pick a legal basis from the reason dropdown.",
    },
    {
      key: "confirm", title: "Confirm what's worth disputing", done: confirmed, tab: "items", action: "Review items",
      what: "AI-flagged items are candidates, not decisions. Your judgment protects the client — disputing accurate items wastes a round and can anger bureaus.",
      how: "For each flagged item, click the green check to Confirm it belongs in a dispute, or the X to Dismiss it. Only confirmed items can go into a letter.",
      tip: "Strongest targets: re-aged collections, unauthorized inquiries, wrong balances, anything past 7 years.",
    },
    {
      key: "dispute", title: "Generate & mail the dispute letter", done: mailed, tab: "disputes", action: "Go to disputes",
      what: "The letter is the legal instrument — it starts the bureau's 30-day FCRA clock. Physical certified mail is the official channel.",
      how: "In the Disputes tab, click 'New Dispute Letter', pick the bureau and template (start with Round 1), tick the confirmed items, and Generate. Read the preview, then 'Send certified mail' and confirm. USPS tracking appears automatically.",
      tip: "One letter per bureau — never mix bureaus in one dispute.",
    },
    {
      key: "outcome", title: "Work the clock & log the outcome", done: resolved, tab: "disputes", action: "Track disputes",
      what: "The bureau has 30 days from delivery. When their response arrives, log the outcome — deleted, updated, verified, or mixed.",
      how: "Watch the 30-day timer on the dispute (green → amber → red). When the response comes, open the dispute, pick the outcome, and click 'Mark resolved'. Verified items? Escalate with a Round 2 letter. Overdue with no response? That's a Round 3 escalation.",
      tip: "Most deletions happen in rounds 1–2. Persistence wins — you have 4 rounds plus FDCPA validation letters.",
    },
  ];
}

export default function CaseGuide({ data, onNavigate }) {
  const [collapsed, setCollapsed] = useState(false);
  const steps = buildSteps(data);
  const doneCount = steps.filter((s) => s.done).length;
  const currentIdx = steps.findIndex((s) => !s.done);
  const allDone = currentIdx === -1;

  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 overflow-hidden" data-testid="case-guide">
      <button onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-emerald-50 transition-colors duration-150 text-left">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <GraduationCap className="h-4 w-4" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900">
              {allDone ? "Case walkthrough complete — nice work!" : `Beginner walkthrough — step ${currentIdx + 1} of ${steps.length}`}
            </div>
            <div className="text-xs text-slate-500">Follow along while you work this client's file. Steps check themselves off automatically.</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5">
            {steps.map((s) => (
              <span key={s.key} className={`h-2 w-2 rounded-full ${s.done ? "bg-emerald-500" : "bg-slate-300"}`} />
            ))}
          </div>
          {collapsed ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronUp className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {!collapsed && (
        <div className="border-t border-emerald-100 bg-white">
          {steps.map((s, idx) => {
            const isCurrent = idx === currentIdx;
            return (
              <div key={s.key} data-testid={`case-guide-step-${idx + 1}`}
                className={`px-5 py-4 border-b border-slate-100 last:border-0 ${isCurrent ? "bg-blue-50/60" : ""} ${s.done ? "opacity-70" : ""}`}>
                <div className="flex items-start gap-3">
                  {s.done
                    ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    : <Circle className={`h-5 w-5 shrink-0 mt-0.5 ${isCurrent ? "text-blue-600" : "text-slate-300"}`} />}
                  <div className="flex-1 min-w-0">
                    <div className={`font-heading font-semibold ${s.done ? "text-slate-500 line-through" : "text-slate-900"}`}>
                      Step {idx + 1}: {s.title}
                    </div>
                    {(isCurrent || (!s.done && !collapsed)) && !s.done && (
                      <div className="mt-2 space-y-2">
                        <p className="text-sm text-slate-700 leading-relaxed"><span className="font-semibold text-slate-500">What & why:</span> {s.what}</p>
                        <p className="text-sm text-slate-700 leading-relaxed"><span className="font-semibold text-slate-500">How:</span> {s.how}</p>
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 inline-block"><b>Pro tip:</b> {s.tip}</p>
                        <div>
                          <Button size="sm" onClick={() => onNavigate(s.tab)} data-testid={`case-guide-action-${idx + 1}`}
                            className={isCurrent ? "bg-[#0B2545] hover:bg-[#134074] text-white" : ""} variant={isCurrent ? "default" : "outline"}>
                            {s.action} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
